import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner, Badge, ProgressBar, Modal, Form, Table, Accordion, Navbar as BootstrapNavbar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import OnboardingUploadModal from '../components/OnboardingUploadModal';
import OnboardingFormModal from '../components/OnboardingFormModal';
import { useDocumentRequirement } from '../contexts/DocumentRequirementContext';
import DocumentCompletionPageV2 from './DocumentCompletionPageV2';

const DocumentCompletionPage = () => {
  const navigate = useNavigate();
  const { setIsCompleted } = useDocumentRequirement();
  const [user, setUser] = useState(null);
  const [documentRequirements, setDocumentRequirements] = useState(null);
  const [packages, setPackages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [myFormSubmissions, setMyFormSubmissions] = useState([]);
  /** Section-based package assignments (same source as DocumentCompletionPageV2). */
  const [mySectionAssignments, setMySectionAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showCompleteFormModal, setShowCompleteFormModal] = useState(false);
  const [activeFormAssignment, setActiveFormAssignment] = useState(null);
  
  // New state for managing uploaded files before submission
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [uploadedFileCategories, setUploadedFileCategories] = useState({});
  const [openAccordions, setOpenAccordions] = useState(new Set());
  /** Item-level progress for sections not yet director-approved (from embedded DocumentCompletionPageV2). */
  const [pendingSectionsProgressReport, setPendingSectionsProgressReport] = useState({
    percent: null,
    totalSections: 0,
    pendingCount: 0
  });
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('theme');
    if (stored) return stored === 'dark';
    return true;
  });

  // Derive submitted files from actual submission data
  const getSubmittedFilesForAssignment = useMemo(() => {
    const submittedFilesMap = {};
    
    // Group submissions by assignment ID
    submissions.forEach(submission => {
      const assignmentId = submission.assignmentId;
      if (assignmentId) {
        if (!submittedFilesMap[assignmentId]) {
          submittedFilesMap[assignmentId] = [];
        }
        
        // Only include approved or submitted (pending review) submissions
        // Rejected submissions should not show as "submitted" in the UI
        if (submission.status === 'approved' || submission.status === 'submitted') {
          // Create a file object that matches the structure expected by the UI
          const fileData = {
            id: submission.id,
            file: {
              name: submission.originalName || 'Submitted File',
              size: submission.fileSize || 0
            },
            category: submission.category || '',
            submittedAt: submission.createdAt,
            status: submission.status
          };
          
          submittedFilesMap[assignmentId].push(fileData);
        }
      }
    });
    
    return submittedFilesMap;
  }, [submissions]);

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    document.body.setAttribute('data-bs-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  const handleSectionAssignmentsChange = useCallback((list) => {
    setMySectionAssignments(Array.isArray(list) ? list : []);
  }, []);

  const handlePendingSectionsProgress = useCallback((report) => {
    if (report && typeof report === 'object') {
      setPendingSectionsProgressReport({
        percent: report.percent != null ? report.percent : null,
        totalSections: report.totalSections ?? 0,
        pendingCount: report.pendingCount ?? 0
      });
    }
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch user data and document requirements
      const userResponse = await axios.get(`${API_ENDPOINTS.AUTH}/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const userData = userResponse.data;
      setUser(userData);
      
      if (userData.documentRequirements) {
        // Parse document requirements if it's a JSON string
        let parsedRequirements = userData.documentRequirements;
        if (typeof userData.documentRequirements === 'string') {
          try {
            parsedRequirements = JSON.parse(userData.documentRequirements);
          } catch (e) {
            console.error('Error parsing document requirements:', e);
            parsedRequirements = { packages: [], documents: [], documentRequests: [], forms: [] };
          }
        }
        if (parsedRequirements && typeof parsedRequirements === 'object') {
          if (!Array.isArray(parsedRequirements.packages)) parsedRequirements.packages = [];
          if (!Array.isArray(parsedRequirements.documents)) parsedRequirements.documents = [];
          if (!Array.isArray(parsedRequirements.documentRequests)) parsedRequirements.documentRequests = [];
          if (!Array.isArray(parsedRequirements.forms)) parsedRequirements.forms = [];
        }
        setDocumentRequirements(parsedRequirements);
        
        console.log('Token being used:', token ? `${token.substring(0, 20)}...` : 'No token');
        console.log('API endpoint:', API_ENDPOINTS.ONBOARDING);
        
        // Fetch packages and documents
        const [packagesRes, documentsRes, assignmentsRes, submissionsRes, sectionAssignmentsRes, myFormSubmissionsRes] = await Promise.all([
          axios.get(`${API_ENDPOINTS.ONBOARDING}/packages`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_ENDPOINTS.ONBOARDING}/documents`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_ENDPOINTS.ONBOARDING}/assignments/me`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_ENDPOINTS.ONBOARDING}/submissions/me`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_ENDPOINTS.ONBOARDING}/package-section-assignments/me`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_ENDPOINTS.ONBOARDING}/form-submissions/me`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        console.log('API Responses:', {
          packages: packagesRes.data,
          documents: documentsRes.data,
          assignments: assignmentsRes.data,
          submissions: submissionsRes.data
        });
        console.log('DocumentCompletionPage - assignments with customName:', assignmentsRes.data?.map(a => ({
          id: a.id,
          customName: a.customName,
          packageId: a.packageId,
          documentId: a.documentId,
          package: a.package?.name,
          document: a.document?.title
        })));
        
        // Check if responses are HTML (error pages)
        if (typeof packagesRes.data === 'string' && packagesRes.data.includes('<!doctype html>')) {
          console.error('Packages API returned HTML instead of JSON');
          setError('Authentication error - please log in again');
          return;
        }
        if (typeof documentsRes.data === 'string' && documentsRes.data.includes('<!doctype html>')) {
          console.error('Documents API returned HTML instead of JSON');
          setError('Authentication error - please log in again');
          return;
        }
        if (typeof assignmentsRes.data === 'string' && assignmentsRes.data.includes('<!doctype html>')) {
          console.error('Assignments API returned HTML instead of JSON');
          setError('Authentication error - please log in again');
          return;
        }
        if (typeof submissionsRes.data === 'string' && submissionsRes.data.includes('<!doctype html>')) {
          console.error('Submissions API returned HTML instead of JSON');
          setError('Authentication error - please log in again');
          return;
        }
        if (typeof sectionAssignmentsRes.data === 'string' && sectionAssignmentsRes.data.includes('<!doctype html>')) {
          console.error('Section assignments API returned HTML instead of JSON');
          setError('Authentication error - please log in again');
          return;
        }

        console.log('User document requirements:', userData.documentRequirements);
        console.log('Assignments found:', assignmentsRes.data?.length || 0);
        console.log('Submissions found:', submissionsRes.data?.length || 0);
        
        // Debug submission structure
        if (submissionsRes.data && submissionsRes.data.length > 0) {
          console.log('Sample submission:', submissionsRes.data[0]);
          console.log('All submissions:', submissionsRes.data);
        }
        
        setPackages(Array.isArray(packagesRes.data) ? packagesRes.data : []);
        setDocuments(Array.isArray(documentsRes.data) ? documentsRes.data : []);
        setAssignments(Array.isArray(assignmentsRes.data) ? assignmentsRes.data : []);
        setSubmissions(Array.isArray(submissionsRes.data) ? submissionsRes.data : []);
        setMySectionAssignments(Array.isArray(sectionAssignmentsRes.data) ? sectionAssignmentsRes.data : []);
        setMyFormSubmissions(Array.isArray(myFormSubmissionsRes.data) ? myFormSubmissionsRes.data : []);

        // Debug package data
        if (packagesRes.data && packagesRes.data.length > 0) {
          console.log('Sample package:', packagesRes.data[0]);
        }
        
        // Debug documents data
        if (documentsRes.data && documentsRes.data.length > 0) {
          console.log('Sample document:', documentsRes.data[0]);
          console.log('Documents with packageId:', documentsRes.data.filter(d => d.packageId));
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Failed to load document requirements');
    } finally {
      setLoading(false);
    }
  };

  // Group assignments by package (similar to onboarding page)
  const groupedAssignments = useMemo(() => {
    if (!Array.isArray(assignments)) return [];
    
    console.log('Processing assignments:', assignments.length);
    console.log('Sample assignment:', assignments[0]);
    console.log('User document requirements:', documentRequirements);
    
    // Filter assignments to only include those relevant to user's document requirements
    // Also include assignments with customNames (duplicates) even if not in current requirements
    const relevantAssignments = assignments.filter(assignment => {
      // Ensure documentRequirements is properly parsed and has the expected structure
      if (!documentRequirements || typeof documentRequirements !== 'object') {
        console.warn('documentRequirements is not properly parsed:', documentRequirements);
        return false;
      }
      
      // Check if this assignment has been submitted or approved - if so, exclude it
      const hasSubmission = submissions.some(submission => {
        if (assignment.packageId && submission.assignment?.packageId === assignment.packageId) {
          return submission.status === 'submitted' || submission.status === 'approved';
        }
        if (assignment.documentId && submission.assignment?.documentId === assignment.documentId) {
          return submission.status === 'submitted' || submission.status === 'approved';
        }
        if (assignment.documentRequestId && submission.assignment?.documentRequestId === assignment.documentRequestId) {
          return submission.status === 'submitted' || submission.status === 'approved';
        }
        return false;
      });
      const hasFormSubmission = myFormSubmissions.some((submission) => {
        if (!assignment.formId) return false;
        if (Number(submission.assignmentId) !== Number(assignment.id)) return false;
        return submission.status === 'submitted' || submission.status === 'approved';
      });
      
      // Exclude assignments that have been submitted or approved
      if (hasSubmission || hasFormSubmission) {
        return false;
      }
      
      // Always include assignments with customNames (duplicates) if not submitted/approved
      if (assignment.customName) {
        return true;
      }
      
      // Check if this assignment is relevant to user's requirements
      let isRelevant = false;
      if (assignment.packageId && documentRequirements.packages?.includes(assignment.packageId)) {
        isRelevant = true;
      }
      if (assignment.documentId && documentRequirements.documents?.includes(assignment.documentId)) {
        isRelevant = true;
      }
      if (assignment.documentRequestId && documentRequirements.documentRequests?.includes(assignment.documentRequestId)) {
        isRelevant = true;
      }
      if (assignment.formId && documentRequirements.forms?.includes(assignment.formId)) {
        isRelevant = true;
      }

      return isRelevant;
    });
    
    console.log('Relevant assignments:', relevantAssignments.length);
    
    // Deduplicate assignments - since we've already filtered out submitted/approved assignments,
    // just keep the most recent assignment for each unique key
    const uniqueAssignments = new Map();
    
    relevantAssignments.forEach(assignment => {
      // For package assignments, include customName in the key to preserve duplicates
      const key = assignment.packageId
        ? `package_${assignment.packageId}_${assignment.customName || 'original'}`
        : assignment.documentRequestId
          ? `request_${assignment.documentRequestId}_${assignment.customName || 'original'}`
          : assignment.formId
            ? `form_${assignment.formId}_${assignment.customName || 'original'}`
            : `document_${assignment.documentId}`;
      
      const existingAssignment = uniqueAssignments.get(key);
      
      // Keep the most recent assignment (highest ID)
      if (!existingAssignment || assignment.id > existingAssignment.id) {
        uniqueAssignments.set(key, assignment);
      }
    });
    
    const finalAssignments = Array.from(uniqueAssignments.values());
    console.log('Final unique assignments:', finalAssignments.length);
    
    const map = new Map();
    
    for (const assignment of finalAssignments) {
      console.log('Processing assignment:', {
        id: assignment.id,
        packageId: assignment.packageId,
        documentId: assignment.documentId,
        hasPackage: !!assignment.package,
        hasDocument: !!assignment.document,
        hasDocumentRequest: !!assignment.documentRequest
      });
      
      // If it's a document assignment, treat it as standalone
      if (assignment.document || assignment.documentId) {
        const key = 'standalone';
        if (!map.has(key)) {
          map.set(key, { 
            key, 
            package: null, 
            packageAssignment: null, 
            documentAssignments: [], 
            documentRequestAssignments: [],
            formAssignments: []
          });
        }
        const group = map.get(key);
        group.documentAssignments.push(assignment);
        console.log('Added document assignment to standalone');
      } else if (assignment.documentRequest || assignment.documentRequestId) {
        // Document requests go to standalone
        const key = 'standalone';
        if (!map.has(key)) {
          map.set(key, { 
            key, 
            package: null, 
            packageAssignment: null, 
            documentAssignments: [], 
            documentRequestAssignments: [],
            formAssignments: []
          });
        }
        const group = map.get(key);
        group.documentRequestAssignments.push(assignment);
        console.log('Added document request assignment to standalone');
      } else if (assignment.form || assignment.formId) {
        const key = 'standalone';
        if (!map.has(key)) {
          map.set(key, {
            key,
            package: null,
            packageAssignment: null,
            documentAssignments: [],
            documentRequestAssignments: [],
            formAssignments: []
          });
        }
        const group = map.get(key);
        group.formAssignments.push(assignment);
        console.log('Added form assignment to standalone');
      } else if (assignment.package || assignment.packageId) {
        // Create separate groups for package assignments with different customNames
        const pkgId = assignment.package?.id || assignment.packageId;
        const customName = assignment.customName || 'original';
        const key = `${pkgId}_${customName}`;
        
        // Find the package data from the packages array
        const packageData = packages.find(p => p.id === pkgId);
        
        if (!map.has(key)) {
          map.set(key, { 
            key, 
            package: packageData, 
            packageAssignment: assignment, 
            documentAssignments: [], 
            documentRequestAssignments: [],
            formAssignments: []
          });
          console.log('Created new package assignment group:', key, 'with customName:', assignment.customName);
        } else {
          // If same key exists, update to most recent assignment
          const group = map.get(key);
          if (assignment.id > group.packageAssignment.id) {
            group.packageAssignment = assignment;
            console.log('Updated package assignment in group:', key, 'to:', assignment.id, 'with customName:', assignment.customName);
          }
        }
        
        // Get the group (either newly created or existing)
        const group = map.get(key);
        
        // Create individual document assignments for each document in the package
        const packageDocuments = documents.filter(doc => doc.packageId === assignment.packageId);
        console.log('Package documents for package', assignment.packageId, ':', packageDocuments);
        
        if (packageDocuments.length > 0) {
          packageDocuments.forEach(doc => {
            const docAssignment = {
              id: `${assignment.id}_doc_${doc.id}`, // Create a unique ID
              programId: assignment.programId,
              packageId: assignment.packageId,
              documentId: doc.id,
              documentRequestId: null,
              assignedToId: assignment.assignedToId,
              dueDate: assignment.dueDate,
              instructions: assignment.instructions,
              status: assignment.status,
              assignedBy: assignment.assignedBy,
              document: doc // Include the document object
            };
            group.documentAssignments.push(docAssignment);
            console.log('Created document assignment:', docAssignment.id, 'for document:', doc.title);
          });
        } else {
          console.log('No package documents found for assignment:', assignment.id);
          console.log('Package data:', assignment.package);
        }
        
        // Create individual document request assignments for each request in the package
        // Note: Document requests are not currently implemented in this version
        // TODO: Add document requests support when needed
        
        console.log('Added package assignment to group:', key);
      }
    }
    
    const result = Array.from(map.values());
    console.log('Final grouped assignments:', result.length, 'groups');
    return result;
  }, [assignments, packages, documents, documentRequirements, submissions, myFormSubmissions]);

  const packageSatisfiedBySections = useCallback((packageId) => {
    if (packageId == null || !mySectionAssignments.length) return false;
    const related = mySectionAssignments.filter((sa) => {
      const pid = sa.packageAssignment?.packageId ?? sa.packageAssignment?.package?.id;
      return pid === packageId;
    });
    if (!related.length) return false;
    return related.every((sa) => sa.status === 'approved');
  }, [mySectionAssignments]);

  /** Hide legacy package upload UI when the same package instance is completed via section assignments (V2). */
  const legacyGroupedAssignments = useMemo(() => {
    if (!Array.isArray(groupedAssignments) || !mySectionAssignments.length) {
      return groupedAssignments;
    }
    const pkgAssignIds = new Set(
      mySectionAssignments.map((sa) => sa.packageAssignment?.id).filter((id) => id != null)
    );
    return groupedAssignments.filter((group) => {
      if (!group.packageAssignment?.id) return true;
      return !pkgAssignIds.has(group.packageAssignment.id);
    });
  }, [groupedAssignments, mySectionAssignments]);

  const docsAndRequestGroups = useMemo(() => {
    return (legacyGroupedAssignments || []).filter((group) =>
      Boolean(group.package) ||
      (group.documentAssignments || []).length > 0 ||
      (group.documentRequestAssignments || []).length > 0
    );
  }, [legacyGroupedAssignments]);

  const standaloneFormAssignments = useMemo(() => {
    return (legacyGroupedAssignments || [])
      .filter((group) => !group.package)
      .flatMap((group) => group.formAssignments || []);
  }, [legacyGroupedAssignments]);

  const sectionsAllApproved = useMemo(() => {
    if (!mySectionAssignments.length) return true;
    return mySectionAssignments.every((sa) => sa.status === 'approved');
  }, [mySectionAssignments]);

  /** Sections still needing director approval or resident action — hide UI when empty. */
  const pendingSectionAssignments = useMemo(
    () => mySectionAssignments.filter((sa) => sa.status !== 'approved'),
    [mySectionAssignments]
  );

  const showPackageSectionsPanel = pendingSectionAssignments.length > 0;

  const getLatestMyFormSubmissionForAssignment = useCallback((assignmentId) => {
    const list = (myFormSubmissions || []).filter((s) => Number(s.assignmentId) === Number(assignmentId));
    if (!list.length) return null;
    return [...list].sort((a, b) => new Date(b.createdAt || b.updatedAt || 0) - new Date(a.createdAt || a.updatedAt || 0))[0];
  }, [myFormSubmissions]);

  const hasIncompleteStandaloneForms = useMemo(() => {
    if (!standaloneFormAssignments.length) return false;
    return standaloneFormAssignments.some((assignment) =>
      !myFormSubmissions.some((submission) =>
        Number(submission.assignmentId) === Number(assignment.id) &&
        (submission.status === 'approved' || submission.status === 'submitted')
      )
    );
  }, [standaloneFormAssignments, myFormSubmissions]);

  // Check if all document requirements are completed
  const allRequirementsCompleted = useMemo(() => {
    if (!documentRequirements) return false;
    if (!sectionsAllApproved) return false;

    const requiredPackageIds = new Set(documentRequirements.packages || []);
    const requiredDocumentIds = new Set(documentRequirements.documents || []);
    const requiredFormIds = new Set(documentRequirements.forms || []);

    if (!assignments.length) {
      const reqPkgs = documentRequirements.packages || [];
      const reqDocs = documentRequirements.documents || [];
      const reqDR = documentRequirements.documentRequests || [];
      const reqForms = documentRequirements.forms || [];
      if (reqDocs.length || reqDR.length || reqForms.length) return false;
      if (!reqPkgs.length) return true;
      return reqPkgs.every((pid) => packageSatisfiedBySections(pid));
    }

    // Get all required package and document IDs
    // Check if all required items have approved submissions
    for (const assignment of assignments) {
      if (assignment.packageId && requiredPackageIds.has(assignment.packageId)) {
        if (packageSatisfiedBySections(assignment.packageId)) continue;
        const hasApprovedSubmission = submissions.some(submission => 
          submission.assignment?.packageId === assignment.packageId && 
          submission.status === 'approved'
        );
        if (!hasApprovedSubmission) return false;
      }
      
      if (assignment.documentId && requiredDocumentIds.has(assignment.documentId)) {
        const hasApprovedSubmission = submissions.some(submission => 
          submission.assignment?.documentId === assignment.documentId && 
          submission.status === 'approved'
        );
        if (!hasApprovedSubmission) return false;
      }
      if (assignment.formId && requiredFormIds.has(assignment.formId)) {
        const hasApprovedForm = myFormSubmissions.some((submission) =>
          Number(submission.assignmentId) === Number(assignment.id) &&
          submission.status === 'approved'
        );
        if (!hasApprovedForm) return false;
      }
    }

    const requiredDocumentRequestIds = documentRequirements.documentRequests || [];
    for (const reqId of requiredDocumentRequestIds) {
      const hasApprovedSubmission = submissions.some(submission =>
        submission.assignment?.documentRequestId === reqId &&
        submission.status === 'approved'
      );
      if (!hasApprovedSubmission) return false;
    }

    return true;
  }, [documentRequirements, assignments, submissions, myFormSubmissions, sectionsAllApproved, mySectionAssignments, packageSatisfiedBySections]);

  // Group documents by package
  const docsByPackageId = useMemo(() => {
    if (!Array.isArray(documents)) return new Map();
    
    const map = new Map();
    for (const doc of documents) {
      const key = doc.packageId || 'standalone';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(doc);
    }
    return map;
  }, [documents]);

  const getAssignmentStatus = (assignment) => {
    // For generated assignment IDs, extract the original assignment ID
    let originalAssignmentId = assignment.id;
    if (typeof assignment.id === 'string' && assignment.id.includes('_doc_')) {
      originalAssignmentId = parseInt(assignment.id.split('_doc_')[0]);
    } else if (typeof assignment.id === 'string' && assignment.id.includes('_request_')) {
      originalAssignmentId = parseInt(assignment.id.split('_request_')[0]);
    }
    
    const submission = submissions.find(sub => sub.assignmentId === originalAssignmentId);
    if (!submission) return 'pending';
    return submission.status;
  };

  const getSubmissionForAssignment = (assignment) => {
    // For generated assignment IDs (like "12_doc_1"), extract the original assignment ID
    let originalAssignmentId = assignment.id;
    if (typeof assignment.id === 'string' && assignment.id.includes('_doc_')) {
      originalAssignmentId = parseInt(assignment.id.split('_doc_')[0]);
    } else if (typeof assignment.id === 'string' && assignment.id.includes('_request_')) {
      originalAssignmentId = parseInt(assignment.id.split('_request_')[0]);
    }
    
    const submission = submissions.find(sub => sub.assignmentId === originalAssignmentId);
    return submission;
  };



  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge bg="success">Approved</Badge>;
      case 'rejected':
        return <Badge bg="danger">Rejected</Badge>;
      case 'submitted':
        return <Badge bg="info">Submitted</Badge>;
      default:
        return <Badge bg="secondary">Pending</Badge>;
    }
  };

  // Check if a document has been submitted
  const isDocumentSubmitted = (docId, assignmentId) => {
    const submittedFiles = getSubmittedFilesForAssignment[assignmentId] || [];
    return submittedFiles.some(file => file.status === 'submitted' || file.status === 'approved');
  };

  const getFileIcon = (fileName, mimeType) => {
    if (mimeType?.includes('pdf')) return 'bi bi-file-earmark-pdf-fill text-danger';
    if (mimeType?.includes('word') || fileName?.match(/\.docx?$/i)) return 'bi bi-file-earmark-word-fill text-primary';
    if (mimeType?.includes('image') || fileName?.match(/\.(jpg|jpeg|png|gif)$/i)) return 'bi bi-image-fill text-info';
    return 'bi bi-file-earmark-fill text-secondary';
  };

  const openUploadModal = (assignment) => {
    setSelectedAssignment(assignment);
    setShowUploadModal(true);
  };

  // New functions for managing uploaded files before submission
  const handleUploadMultiple = async (filesWithCategories) => {
    try {
      setUploading(true);
      
      if (selectedAssignment) {
        // Store files for assignment submission instead of uploading immediately
        const assignmentId = selectedAssignment.id;
        const existingFiles = uploadedFiles[assignmentId] || [];
        const newFiles = filesWithCategories.map(({ file, category }) => ({
          file,
          category: category || '',
          id: Date.now() + Math.random() // Generate unique ID for each file
        }));
        
        setUploadedFiles(prev => ({
          ...prev,
          [assignmentId]: newFiles // Replace existing files instead of adding to them
        }));
        
        // Clear existing categories for this assignment and initialize new ones
        setUploadedFileCategories(prev => {
          const newState = { ...prev };
          // Remove existing categories for this assignment
          Object.keys(newState).forEach(key => {
            const existingFiles = uploadedFiles[assignmentId] || [];
            if (existingFiles.some(f => f.id.toString() === key)) {
              delete newState[key];
            }
          });
          // Add new categories
          newFiles.forEach(({ id, category }) => {
            newState[id] = category;
          });
          return newState;
        });
        
        // Auto-open accordion for standalone document assignments
        if (selectedAssignment.document && !selectedAssignment.package) {
          const accordionKey = `assign-${selectedAssignment.id}-0`;
          console.log('Opening accordion with key:', accordionKey);
          setOpenAccordions(prev => {
            const newSet = new Set([...prev, accordionKey]);
            console.log('Updated openAccordions:', Array.from(newSet));
            return newSet;
          });
        }
      }
      
      setShowUploadModal(false);
    } catch (e) { setError(e.message); } finally { setUploading(false); }
  };

  const removeUploadedFile = (assignmentId, fileId) => {
    setUploadedFiles(prev => {
      const assignmentFiles = prev[assignmentId] || [];
      const updatedFiles = assignmentFiles.filter(f => f.id !== fileId);
      if (updatedFiles.length === 0) {
        const newState = { ...prev };
        delete newState[assignmentId];
        return newState;
      }
      return { ...prev, [assignmentId]: updatedFiles };
    });
    
    setUploadedFileCategories(prev => {
      const newState = { ...prev };
      delete newState[fileId];
      return newState;
    });
  };

  const submitUploadedFiles = async (assignmentId) => {
    try {
      const files = uploadedFiles[assignmentId] || [];
      if (files.length === 0) return;
      
      setUploading(true);
      const token = localStorage.getItem('token');
      
      // Submit each file individually
      for (const fileData of files) {
      const formData = new FormData();
        formData.append('file', fileData.file);
        
        await axios.post(`${API_ENDPOINTS.ONBOARDING}/assignments/${assignmentId}/submit`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      }
      
      // Clear uploaded files for this assignment
      setUploadedFiles(prev => {
        const newState = { ...prev };
        delete newState[assignmentId];
        return newState;
      });
      
      // Clear categories for this assignment's files
      setUploadedFileCategories(prev => {
        const newState = { ...prev };
        files.forEach(({ id }) => delete newState[id]);
        return newState;
      });
      
      // Close accordion for standalone document assignments after submission
      const accordionKey = `assign-${assignmentId}-0`;
      setOpenAccordions(prev => {
        const newSet = new Set(prev);
        newSet.delete(accordionKey);
        return newSet;
      });
      
      await fetchUserData();
    } catch (e) { setError(e.message); } finally { setUploading(false); }
  };

  const handleSubmitFormAssignment = async (assignment, formData) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/assignments/${assignment.id}/submit-form`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ formData })
    });
    if (!res.ok) {
      const payload = await res.json().catch(() => ({}));
      throw new Error(payload?.error || 'Failed to submit form');
    }
    await fetchUserData();
  };



  /** Section slice of the top bar: only non–director-approved work; 100% when none pending. */
  const effectiveSectionProgressPercent = useMemo(() => {
    if (!mySectionAssignments.length) return null;
    if (pendingSectionAssignments.length === 0) return 100;
    const p = pendingSectionsProgressReport?.percent;
    return p != null ? p : 0;
  }, [
    mySectionAssignments.length,
    pendingSectionAssignments.length,
    pendingSectionsProgressReport?.percent
  ]);

  const getProgressPercentage = () => {
    // If all requirements are completed, return 100%
    if (allRequirementsCompleted && !hasIncompleteStandaloneForms) return 100;

    const sectionPct = effectiveSectionProgressPercent;
    const totalFormItems = standaloneFormAssignments.length;
    const completedFormItems = standaloneFormAssignments.filter((assignment) =>
      myFormSubmissions.some((submission) =>
        Number(submission.assignmentId) === Number(assignment.id) &&
        (submission.status === 'approved' || submission.status === 'submitted')
      )
    ).length;
    const formPct = totalFormItems > 0 ? Math.round((completedFormItems / totalFormItems) * 100) : null;

    // If no legacy groups but there are submissions, calculate progress based on submissions
    if (!legacyGroupedAssignments || legacyGroupedAssignments.length === 0) {
      if (sectionPct != null && formPct != null) return Math.round((sectionPct + formPct) / 2);
      if (sectionPct != null) return sectionPct;
      if (formPct != null) return formPct;
      if (!submissions || submissions.length === 0) return 0;

      const totalSubmissions = submissions.length;
      const completedSubmissions = submissions.filter(submission =>
        submission.status === 'approved' || submission.status === 'submitted'
      ).length;

      if (totalSubmissions === 0) return 0;

      return Math.round((completedSubmissions / totalSubmissions) * 100);
    }

    let totalItems = 0;
    let completedItems = 0;

    // Count all assignments including package assignments (legacy groups only)
    legacyGroupedAssignments.forEach((group, groupIndex) => {
      // Count package assignments (only count the package itself, not individual documents within it)
      if (group.packageAssignment) {
        totalItems++;
        const submission = getSubmissionForAssignment(group.packageAssignment);
        const isCompleted = submission && (submission.status === 'approved' || submission.status === 'submitted');
        if (isCompleted) completedItems++;
        
        // Don't count individual document assignments within packages - they're part of the package
      } else {
        // Only count individual document assignments if they're standalone (not part of a package)
        group.documentAssignments.forEach((assignment, docIndex) => {
        totalItems++;
        const submission = getSubmissionForAssignment(assignment);
        const isCompleted = submission && (submission.status === 'approved' || submission.status === 'submitted');
        if (isCompleted) completedItems++;
      });
      }
      
      // Count document request assignments
      group.documentRequestAssignments.forEach((assignment, reqIndex) => {
        totalItems++;
        const submission = getSubmissionForAssignment(assignment);
        const isCompleted = submission && (submission.status === 'approved' || submission.status === 'submitted');
        if (isCompleted) completedItems++;
      });
    });

    totalItems += totalFormItems;
    completedItems += completedFormItems;
    
    if (totalItems === 0) {
      return sectionPct != null ? sectionPct : 0;
    }

    const legacyPercentage = Math.round((completedItems / totalItems) * 100);
    if (sectionPct != null && mySectionAssignments.length > 0) {
      return Math.round((sectionPct + legacyPercentage) / 2);
    }
    if (sectionPct != null) return sectionPct;
    return legacyPercentage;
  };

  const canAccessApp = () => {
    if (hasIncompleteStandaloneForms) return false;
    if (!sectionsAllApproved) return false;

    // No legacy accordion groups (e.g. only V2 package sections, or filtered empty): still require
    // director-approved submissions for every required document/request/package via allRequirementsCompleted.
    if (!legacyGroupedAssignments || legacyGroupedAssignments.length === 0) {
      return allRequirementsCompleted;
    }

    let totalItems = 0;
    let completedItems = 0;

    // Count all assignments including package assignments
    legacyGroupedAssignments.forEach(group => {
      // Count package assignments (only count the package itself, not individual documents within it)
      if (group.packageAssignment) {
        totalItems++;
        const submission = getSubmissionForAssignment(group.packageAssignment);
        const isCompleted = submission && submission.status === 'approved';
        if (isCompleted) completedItems++;
        
        // Don't count individual document assignments within packages - they're part of the package
      } else {
        // Only count individual document assignments if they're standalone (not part of a package)
      group.documentAssignments.forEach(assignment => {
        totalItems++;
        const submission = getSubmissionForAssignment(assignment);
          const isCompleted = submission && submission.status === 'approved';
        if (isCompleted) completedItems++;
      });
      }
      
      // Count document request assignments
      group.documentRequestAssignments.forEach(assignment => {
        totalItems++;
        const submission = getSubmissionForAssignment(assignment);
        const isCompleted = submission && submission.status === 'approved';
        if (isCompleted) completedItems++;
      });
    });
    
    // All items must be approved by director
    return totalItems > 0 && completedItems === totalItems;
  };

  const hasSubmittedDocuments = () => {
    // Check if any documents have been submitted but not yet approved
    // Use submissions data directly instead of relying on groupedAssignments
    if (!submissions || submissions.length === 0) return false;
    
    return submissions.some(submission => submission.status === 'submitted');
  };

  const handleContinueToApp = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Mark user as having completed document requirements
      await axios.put(`${API_ENDPOINTS.AUTH}/me`, {
        documentRequirementsCompleted: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update context and localStorage
      setIsCompleted(true);
      console.log('Set document requirements as completed');
      
      // Navigate to dashboard
      const role = localStorage.getItem('role');
      const dashboardPath = role === 'director' ? '/dashboard' : `/${role}/dashboard`;
      navigate(dashboardPath);
    } catch (error) {
      console.error('Error marking document requirements as completed:', error);
      // Still navigate to dashboard even if the update fails
      const role = localStorage.getItem('role');
      const dashboardPath = role === 'director' ? '/dashboard' : `/${role}/dashboard`;
      navigate(dashboardPath);
    }
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <Spinner animation="border" className="text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <Alert variant="danger">{error}</Alert>
      </div>
    );
  }

  if (!documentRequirements || (!documentRequirements.packages?.length && !documentRequirements.documents?.length && !documentRequirements.documentRequests?.length && !documentRequirements.forms?.length)) {
    // No document requirements, redirect to dashboard
    console.log('No document requirements found, redirecting to dashboard');
    const role = localStorage.getItem('role');
    const dashboardPath = role === 'director' ? '/dashboard' : `/${role}/dashboard`;
    navigate(dashboardPath);
    return null;
  }


  // If user has document requirements but no assignments, check if they're all completed
  if (groupedAssignments.length === 0) {
    console.log('User has document requirements but no assignments found');
    
    // If all requirements are completed, show completion message
    if (allRequirementsCompleted) {
      console.log('All document requirements completed, showing completion message');
    }
  }

  const progressPercentage = getProgressPercentage();

  return (
    <>
      <div className="bg-dark text-white">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
            <BootstrapNavbar bg="dark" variant="dark" expand="lg" className="mb-0">
            <Container className="d-flex flex-row align-items-center justify-content-between">
          
          <BootstrapNavbar.Brand
          >
            <img src="/images/asset4.svg" alt="brand logo" style={{ height: "16px", marginTop: "-8px"}}/>
            {/*<img src="/images/logo4.svg" alt="brand logo" style={{ width: "32px", height: "32px", marginTop: "-4px"}}/>
            <span className="ms-2">medhive</span>*/}
          </BootstrapNavbar.Brand>


                  <div className=" d-flex flex-row align-items-center justify-content-end w-50 w-md-auto" >
                     <ProgressBar 
                       now={progressPercentage} 
                       variant= "success-solid"
                       className="mb-0"
                     />
                   
                     <div className="d-flex flex-row gap-2 align-items-end">
                    <div className="h6 mb-0 ms-2">{progressPercentage}%</div>
                    <small className="opacity-75 d-none d-md-block">Complete</small>
                  </div></div>
                  <div>
                  <Button
                    variant="light-lite"
                    size="sm"
                    title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                    onClick={() => setDarkMode((prev) => !prev)}
                  >
                    {darkMode ? (
                      <i className="bi bi-sun-fill custom-btn"></i>
                    ) : (
                      <i className="bi bi-moon-fill custom-btn"></i>
                    )}
                  </Button>
                  <Button 
                    variant="light-lite" 
                    size="sm"
                    onClick={() => {
                      localStorage.removeItem("token");
                      localStorage.removeItem("firstName");
                      localStorage.removeItem("lastName");
                      localStorage.removeItem("profilePic");
                      localStorage.removeItem("role");
                      localStorage.removeItem("documentRequirementsCompleted");
                      localStorage.removeItem("notifications");
                      localStorage.removeItem("lastNotificationTime");
                      window.dispatchEvent(new CustomEvent('app-logout'));
                      window.location.href = "/";
                    }}
                  >
                    <i className="bi bi-box-arrow-right custom-btn"></i>
                  </Button></div>
              
          </Container>
          </BootstrapNavbar>
            </Col>
          </Row>
        </Container>
      </div>
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col lg={10}>
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-body py-4 border-bottom">
                <h4 className="mb-0">
                  <i className="bi bi-file-earmark-check me-2"></i>
                  Document Requirements
                </h4>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="p-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5>Welcome, {user?.firstName} {user?.lastName}!</h5>
                    </div>
                  <p className="text-muted">
                    Complete package sections below when assigned. Upload standalone documents and document requests in the following sections. Once everything is submitted and your program approves it, you can access the application.
                  </p>
                </div>

                {showPackageSectionsPanel && (
                  <div className="border-bottom bg-body-secondary bg-opacity-25">
                    <div className="px-3 pt-3 pb-2">
                      <h5 className="h6 text-uppercase text-muted mb-0">Package sections</h5>
                      <small className="text-muted">Work through each section; save drafts and submit for review when ready.</small>
                    </div>
                    <DocumentCompletionPageV2
                      hideNavbar
                      embedded
                      showContinueToApp={false}
                      onSectionAssignmentsChange={handleSectionAssignmentsChange}
                      hideApprovedSections
                      onPendingSectionsProgress={handlePendingSectionsProgress}
                    />
                  </div>
                )}

                {/* Assignments (standalone documents & document requests, and legacy package uploads) */}
                {legacyGroupedAssignments.length === 0 ? (
                  showPackageSectionsPanel ? null : (
                  allRequirementsCompleted ? (
                    <div className="text-center px-3 py-5">
                      <div className="text-success">
                        <i className="bi bi-check-circle fs-1"></i>
                      </div>
                      <h5 className="text-success mb-3">All Document Requirements Completed!</h5>
                      <p className="text-muted mb-4">
                        Congratulations! You have successfully completed all required documents. 
                        Your submissions have been approved and you can now access the full application.
                      </p>

                    </div>
                  ) : hasSubmittedDocuments() ? (
                    <div className="text-center px-3 py-5">
                      <div className="text-warning">
                        <i className="bi bi-clock-history fs-1"></i>
                      </div>
                      <h5 className="text-warning mb-3">Documents Submitted - Pending Approval</h5>
                      <p className="text-muted mb-4">
                        Your documents have been submitted and are currently under review by your program director. 
                        You will be notified once the review is complete.
                      </p>
                      <Button 
                        variant="outline-primary" 
                        onClick={() => window.location.reload()}
                        className="mt-3"
                      >
                        <i className="bi bi-arrow-clockwise me-2"></i>
                        Refresh Page
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center px-3 py-5">
                      <div className="text-muted">
                        <i className="bi bi-exclamation-triangle fs-1"></i>
                      </div>
                      <h6 className="text-muted">No Document Requirements Found</h6>
                      <p className="text-muted">
                          You do not have any document requirements assigned at this time.
                          Please contact your program director if you believe this is an error.
                      </p>
                      <p className="text-muted small mb-4">
                        If nothing is assigned for you to complete, you can continue into the application.
                      </p>
                      <div className="d-flex flex-wrap justify-content-center gap-2 mt-3">
                        <Button
                          variant="success"
                          onClick={handleContinueToApp}
                        >
                          Continue to Application
                          <i className="bi bi-arrow-right ms-2"></i>
                        </Button>
                        <Button
                          variant="outline-primary"
                          onClick={() => window.location.reload()}
                        >
                          <i className="bi bi-arrow-clockwise me-2"></i>
                          Refresh Page
                        </Button>
                      </div>
                    </div>
                  )
                )
                )
                : (
                  <>
                  {docsAndRequestGroups.length > 0 && (
                    <>
                  <div className="px-3 pt-3 pb-1">
                    <h5 className="h6 text-uppercase text-muted mb-0">Documents &amp; document requests</h5>
                    <small className="text-muted">Upload files for each assignment below.</small>
                  </div>
                  <Accordion defaultActiveKey={docsAndRequestGroups.map((g, idx) => `${g.key}-${idx}`)} alwaysOpen>
                    {docsAndRequestGroups.map((group, idx) => {
                      const eventKey = `${group.key}-${idx}`;
                      const headerTitle = (() => {
                        const title = group.packageAssignment?.customName || (group.package ? group.package.name : 'Standalone Documents');
                        console.log(`DocumentCompletionPage - Package assignment header for group ${group.key}:`, { 
                          packageAssignmentId: group.packageAssignment?.id,
                          customName: group.packageAssignment?.customName, 
                          packageName: group.package?.name,
                          finalTitle: title 
                        });
                        return title;
                      })();
                      const headerAssignment = group.packageAssignment || group.documentAssignments[0] || null;
                      const headerStatus = headerAssignment ? getAssignmentStatus(headerAssignment) : 'pending';
                      const headerDue = headerAssignment && headerAssignment.dueDate ? 
                        `Due ${new Date(headerAssignment.dueDate).toLocaleDateString()}` : 'No due date';
                      const packageId = group.package ? group.package.id : null;
                      const packageDocs = packageId ? (docsByPackageId.get(packageId) || []) : [];
                      
                      let totalItems, completedItems, pendingItems;
                      
                      if (group.package) {
                        // For package assignments, count documents in the package
                        totalItems = packageDocs.length + group.documentRequestAssignments.length;
                        
                        // Count completed documents by checking submissions
                        // For package assignments, we check if there are any submissions for the assignment
                        const assignmentSubmissions = submissions.filter(s => 
                          s.assignmentId === group.packageAssignment?.id
                        );
                        
                        // If there are any approved/submitted submissions for this assignment, consider it completed
                        const hasCompletedSubmissions = assignmentSubmissions.some(s => 
                          s.status === 'approved' || s.status === 'submitted'
                        );
                        
                        const completedDocs = hasCompletedSubmissions ? packageDocs.length : 0;
                        const completedRequests = group.documentRequestAssignments.filter(a => {
                        const submission = submissions.find(sub => sub.assignmentId === a.id);
                          return submission && (submission.status === 'approved' || submission.status === 'submitted');
                        }).length;
                        completedItems = completedDocs + completedRequests;
                      } else {
                        // For standalone assignments
                        totalItems = group.documentAssignments.length + group.documentRequestAssignments.length;
                        completedItems = group.documentAssignments.filter(a => {
                          const submission = submissions.find(sub => sub.assignmentId === a.id);
                          return submission && (submission.status === 'approved' || submission.status === 'submitted');
                      }).length + group.documentRequestAssignments.filter(a => {
                        const submission = submissions.find(sub => sub.assignmentId === a.id);
                          return submission && (submission.status === 'approved' || submission.status === 'submitted');
                      }).length;
                      }
                      
                      pendingItems = totalItems - completedItems;

                      return (
                        <Accordion.Item eventKey={eventKey} key={eventKey} className="border-0 rounded-0">
                          <div className="d-flex align-items-center p-3">
                            <div className="d-flex flex-row gap-2 align-items-center flex-grow-1">
                              <div>
                                <Badge bg="secondary" className="rounded-2 p-2">
                                  <i className="bi bi-folder-fill text-warning fs-4"></i>
                                </Badge>
                              </div>
                              <div>
                                <div className="fw-semibold">{headerTitle}</div>
                                <small className="text-muted">{headerDue}</small>
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-2 me-3 d-none d-md-flex">
                              {/* Only show badges if not all documents are approved */}
                              {(() => {
                                const allApproved = completedItems === totalItems && totalItems > 0;
                                return allApproved ? null : (
                                  <>
                              <Badge bg="secondary">{totalItems} total</Badge>
                              <Badge bg="warning" text="dark">{pendingItems} pending</Badge>
                              <Badge bg="success">{completedItems} completed</Badge>
                                  </>
                                );
                              })()}
                              {getStatusBadge(headerStatus)}
                              {group.packageAssignment && (
                                <div>
                                  {(() => {
                                    const hasUploadedFiles = (uploadedFiles[group.packageAssignment.id] || []).length > 0;
                                    const hasSubmittedFiles = (getSubmittedFilesForAssignment[group.packageAssignment.id] || []).length > 0;
                                    return hasSubmittedFiles ? (
                                      <Badge bg="success" className="ms-2">
                                        <i className="bi bi-check-circle-fill me-1"></i>
                                        Submitted
                                      </Badge>
                                    ) : hasUploadedFiles ? (
                                      <Badge bg="warning" className="ms-2">
                                        <i className="bi bi-clock me-1"></i>
                                        Files Ready
                                      </Badge>
                                    ) : (
                                <Button 
                                  size="sm" 
                                  variant="outline-primary" 
                                  onClick={() => openUploadModal(group.packageAssignment)}
                                >
                                  Upload
                                </Button>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                            <Accordion.Button as="div" className="border-0 bg-transparent p-0" style={{width: 'auto'}}>
                            </Accordion.Button>
                          </div>
                          <Accordion.Body>
                            {group.package && group.package.description && (
                              <div className="mb-3 text-muted">{group.package.description}</div>
                            )}
                            {group.packageAssignment && group.packageAssignment.instructions && (
                              <div className="alert alert-light mb-3">
                                <strong>Instructions:</strong><br />
                                {group.packageAssignment.instructions}
                              </div>
                            )}

                            {group.package ? (
                              <>
                                {packageDocs.length > 0 ? (
                                  <Table size="sm" hover responsive>
                                    <thead>
                                      <tr>
                                        <th>Type</th>
                                        <th className="text-start">Document</th>
                                        <th className="text-end">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                                                             {packageDocs.map((doc) => {
                                         const docAssign = group.documentAssignments.find(a => 
                                           a.document && a.document.id === doc.id
                                         ) || null;
                                         const status = docAssign ? getAssignmentStatus(docAssign) : 'pending';
                                         const submission = docAssign ? getSubmissionForAssignment(docAssign) : null;
                                         
                                         const isSubmitted = docAssign && isDocumentSubmitted(doc.id, docAssign.id);
                                         
                                         return (
                                           <tr key={`doc-${doc.id}`} className={isSubmitted ? 'table-secondary' : ''}>
                                             <td>
                                               <Badge bg="secondary" className="rounded-2 p-2">
                                                 <i className={`${getFileIcon(doc.originalName, doc.mimeType)} fs-5`}></i>
                                               </Badge>
                                             </td>
                                             <td className="text-start text-break">
                                               <div className="d-flex align-items-center gap-2">
                                               <div>{doc.title}</div>
                                                 {isSubmitted && (
                                                   <Badge bg="info" className="ms-2">
                                                     <i className="bi bi-check-circle me-1"></i>
                                                     Submitted
                                                   </Badge>
                                                 )}
                                               </div>
                                             </td>
                                             <td className="text-end">
                                               {doc.webPath && (
                                                 <Button 
                                                   size="sm" 
                                                   variant="light" 
                                                   onClick={async (e) => { 
                                                     e.preventDefault(); 
                                                     const url = `${API_ENDPOINTS.PROFILE_PICS}${doc.webPath}`; 
                                                     const r = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }); 
                                                     const blob = await r.blob(); 
                                                     const dl = URL.createObjectURL(blob); 
                                                     const link = document.createElement('a'); 
                                                     link.href = dl; 
                                                     link.download = doc.originalName || doc.title; 
                                                     document.body.appendChild(link); 
                                                     link.click(); 
                                                     document.body.removeChild(link); 
                                                     URL.revokeObjectURL(dl); 
                                                   }}
                                                 >
                                                   <i className="bi bi-download"></i>
                                                 </Button>
                                               )}
                                               {/*{(docAssign || group.packageAssignment) ? (
                                                 submission ? (
                                                   <Button 
                                                     size="sm" 
                                                     variant="outline-primary" 
                                                     onClick={() => openUploadModal(docAssign || group.packageAssignment)}
                                                   >
                                                     <i className="bi bi-arrow-repeat"></i> Re-upload
                                                   </Button>
                                                 ) : (
                                                   <Button 
                                                     size="sm" 
                                                     variant="outline-primary" 
                                                     onClick={() => openUploadModal(docAssign || group.packageAssignment)}
                                                   >
                                                     Upload
                                                   </Button>
                                                 )
                                               ) : (
                                                 <span className="text-muted">—</span>
                                               )*/}
                                             </td>
                                           </tr>
                                         );
                                       })}
                                    </tbody>
                                  </Table>
                                ) : (
                                  <div className="text-muted">No documents found for this package.</div>
                                )}
                                
                                {/* Uploaded Files for Package Assignment */}
                                {group.packageAssignment && (() => {
                                  const assignmentFiles = uploadedFiles[group.packageAssignment.id] || [];
                                  const submittedAssignmentFiles = getSubmittedFilesForAssignment[group.packageAssignment.id] || [];
                                  const hasUploadedFiles = assignmentFiles.length > 0;
                                  const hasSubmittedFiles = submittedAssignmentFiles.length > 0;
                                  
                                  return (hasUploadedFiles || hasSubmittedFiles) ? (
                                    <div className="mt-4">
                                      {hasUploadedFiles && (
                                        <>
                                          <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h6 className="mb-0">Uploaded Files</h6>
                                            <Button 
                                              size="sm" 
                                              variant="success" 
                                              onClick={() => submitUploadedFiles(group.packageAssignment.id)}
                                              disabled={uploading}
                                            >
                                              {uploading ? 'Submitting...' : 'Submit to Director'}
                                            </Button>
                                          </div>
                                          <div className="list-group">
                                            {assignmentFiles.map((fileData) => (
                                              <div key={fileData.id} className="list-group-item d-flex justify-content-between align-items-center">
                                                <div className="d-flex align-items-center gap-2">
                                                  <i className={`${getFileIcon(fileData.file.name)} fs-5`}></i>
                                                  <span>{fileData.file.name}</span>
                                                  <small className="text-muted">({(fileData.file.size / 1024).toFixed(1)} KB)</small>
                                                </div>
                                                <Button 
                                                  size="sm" 
                                                  variant="outline-danger" 
                                                  onClick={() => removeUploadedFile(group.packageAssignment.id, fileData.id)}
                                                >
                                                  <i className="bi bi-trash"></i>
                                                </Button>
                                              </div>
                                            ))}
                                          </div>
                                        </>
                                      )}
                                      
                                      {hasSubmittedFiles && (
                                        <>
                                          <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h6 className="mb-0">
                                              Submitted Files
                                            </h6>
                                          </div>
                                          <div className="list-group">
                                            {submittedAssignmentFiles.map((fileData) => (
                                              <div key={fileData.id} className="list-group-item d-flex justify-content-between align-items-center bg-light">
                                                <div className="d-flex align-items-center gap-2">
                                                  <i className={`${getFileIcon(fileData.file.name)} fs-5 text-muted`}></i>
                                                  <span className="text-muted">{fileData.file.name}</span>
                                                  <small className="text-muted">({(fileData.file.size / 1024).toFixed(1)} KB)</small>                                                
                                                  </div>
                                                  <Badge bg={fileData.status === 'approved' ? 'success' : fileData.status === 'rejected' ? 'danger' : 'info'} className="ms-2">
                                                    {fileData.status}
                                                  </Badge>
                                              </div>
                                            ))}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  ) : null;
                                })()}
                              </>
                            ) : (
                              <>
                                {/* Standalone Documents */}
                                {group.documentAssignments.length > 0 && (
                                  <div className="mt-3">
                                    <h6 className="mb-3">Below are the standalone documents you need to upload for your program.</h6>
                                    <Accordion activeKey={Array.from(openAccordions)} onSelect={(key) => {
                                      setOpenAccordions(prev => {
                                        const newSet = new Set(prev);
                                        if (newSet.has(key)) {
                                          newSet.delete(key);
                                        } else {
                                          newSet.add(key);
                                        }
                                        return newSet;
                                      });
                                    }}>
                                      {group.documentAssignments.map((assignment, idx) => (
                                        <Accordion.Item key={`assign-${assignment.id}`} eventKey={`assign-${assignment.id}-${idx}`} className="border-0 custom-accordian-border mb-2">
                                          <div className="d-flex flex-row align-items-center justify-content-between py-3">
                                            <div className="d-flex flex-row  align-items-center justify-content-between w-100 gap-2" >
                                            <div className="d-flex flex-md-row flex-column gap-md-4 gap-2 align-items-center">
                                              <div className="d-flex flex-row align-items-center gap-2 me-3 me-md-0">
                                                <Badge bg="secondary" className="rounded-2 p-2">
                                                    <i className={`${getFileIcon(assignment.document?.originalName, assignment.document?.mimeType)} fs-4`}></i>
                                                </Badge>

                                                <div>
                                                  <div className="fw-semibold">
                                                    {(() => {
                                                      const displayName = assignment.customName || assignment.document?.title || assignment.package?.name || assignment.documentRequest?.title;
                                                      console.log(`DocumentCompletionPage - Assignment ${assignment.id} display name:`, { customName: assignment.customName, displayName });
                                                      return displayName;
                                                    })()}
                                                  </div>
                                                  <small className="text-muted">Uploaded {assignment.document?.createdAt ? new Date(assignment.document.createdAt).toLocaleString() : '—'}</small>
                                                </div>
                                              </div>
                                              <div className="d-flex flex-row align-items-center gap-2 me-3 me-md-0">
                                                <Badge bg={getAssignmentStatus(assignment) === 'completed' ? 'success' : 'secondary'}>{getAssignmentStatus(assignment)}</Badge>
                                                <Badge bg="secondary">{assignment.document?.category || 'No category'}</Badge>
                                                <Badge bg="light" text="dark">{assignment.document?.fileSize ? `${(assignment.document.fileSize / 1024).toFixed(1)} KB` : '—'}</Badge>
                                              </div>
                                            </div>
                                            <div className="d-flex flex-sm-row flex-column align-items-sm-center align-items-start justify-content-end gap-2 me-3">
                                              {assignment.document?.webPath && (
                                                <Button size="sm" variant="light" onClick={async (e) => { e.preventDefault(); const url = `${API_ENDPOINTS.PROFILE_PICS}${assignment.document.webPath}`; const r = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }); const blob = await r.blob(); const dl = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = dl; link.download = assignment.document.originalName || assignment.document.title; document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(dl); }}><i className="bi bi-download"></i></Button>
                                              )}
                                                                                             {(() => {
                                                 const hasUploadedFiles = (uploadedFiles[assignment.id] || []).length > 0;
                                                 const hasSubmittedFiles = (getSubmittedFilesForAssignment[assignment.id] || []).length > 0;
                                                 return hasSubmittedFiles ? (
                                                   <Badge bg="success" size="sm">
                                                     <i className="bi bi-check-circle-fill me-1"></i>
                                                     Submitted
                                                   </Badge>
                                                 ) : hasUploadedFiles ? (
                                                   <Badge bg="warning" size="sm">
                                                     <i className="bi bi-clock me-1"></i>
                                                     Files Ready
                                                   </Badge>
                                                 ) : (
                                                   <Button size="sm" variant="outline-primary" className="d-flex flex-inline" onClick={() => openUploadModal(assignment)}><i className="bi bi-cloud-upload "></i> <span className="d-none d-sm-inline ms-2">Upload</span></Button>
                                                 );
                                               })()}
                                            </div></div>

                                            <Accordion.Button as="div" className="border-0 bg-transparent p-0" style={{width: 'auto'}}>
                                            </Accordion.Button>
                                          </div>
                                          <Accordion.Body>
                                            {assignment.instructions && (
                                              <div className="alert alert-light mb-3">
                                                <strong>Instructions:</strong><br />
                                                {assignment.instructions}
                                              </div>
                                            )}
                                            <div className="d-flex justify-content-between align-items-center">
                                                       <small className="text-muted">
                                                Due: {assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 'No due date'}
                                                       </small>
                                                     </div>
                                            
                                                                                         {/* Uploaded Files for Standalone Document Assignment */}
                                             {(() => {
                                               const assignmentFiles = uploadedFiles[assignment.id] || [];
                                               const submittedAssignmentFiles = getSubmittedFilesForAssignment[assignment.id] || [];
                                               const hasUploadedFiles = assignmentFiles.length > 0;
                                               const hasSubmittedFiles = submittedAssignmentFiles.length > 0;
                                               
                                               return (hasUploadedFiles || hasSubmittedFiles) ? (
                                                 <div className="mt-3">
                                                   {hasUploadedFiles && (
                                                     <>
                                                       <div className="d-flex justify-content-between align-items-center mb-2">
                                                         <h6 className="mb-0 fs-6">Uploaded Files</h6>
                                                     <Button 
                                                       size="sm" 
                                                           variant="success" 
                                                           onClick={() => submitUploadedFiles(assignment.id)}
                                                           disabled={uploading}
                                                     >
                                                           {uploading ? 'Submitting...' : 'Submit'}
                                                     </Button>
                                                   </div>
                                                       <div className="list-group">
                                                         {assignmentFiles.map((fileData) => (
                                                           <div key={fileData.id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                                                             <div className="d-flex align-items-center gap-2">
                                                               <i className={`${getFileIcon(fileData.file.name)} fs-6`}></i>
                                                               <span className="small">{fileData.file.name}</span>
                                                               <small className="text-muted">({(fileData.file.size / 1024).toFixed(1)} KB)</small>
                                                 </div>
                                                 <Button 
                                                   size="sm" 
                                                               variant="outline-danger" 
                                                               onClick={() => removeUploadedFile(assignment.id, fileData.id)}
                                                             >
                                                               <i className="bi bi-trash"></i>
                                                 </Button>
                                                           </div>
                                                         ))}
                                                       </div>
                                                     </>
                                                   )}
                                                   
                                                   {hasSubmittedFiles && (
                                                     <>
                                                       <div className="d-flex justify-content-between align-items-center mb-2">
                                                         <h6 className="mb-0 fs-6">
                                                           Submitted Files
                                                         </h6>
                                                       </div>
                                                       <div className="list-group">
                                                         {submittedAssignmentFiles.map((fileData) => (
                                                           <div key={fileData.id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                                                             <div className="d-flex align-items-center gap-2">
                                                               <i className={`${getFileIcon(fileData.file.name)} fs-6 text-muted`}></i>
                                                               <span className="small text-muted">{fileData.file.name}</span>
                                                               <small className="text-muted">({(fileData.file.size / 1024).toFixed(1)} KB)</small>
                                                              </div> 
                                                               <Badge bg={fileData.status === 'approved' ? 'success' : fileData.status === 'rejected' ? 'danger' : 'info'} className="ms-1">
                                                                 {fileData.status}
                                                               </Badge>
                                                           </div>
                                                         ))}
                                                       </div>
                                                     </>
                                                   )}
                                                 </div>
                                               ) : null;
                                             })()}
                                          </Accordion.Body>
                                        </Accordion.Item>
                                      ))}
                                    </Accordion>
                                  </div>
                                )}

                                {/* Document Requests */}
                                {group.documentRequestAssignments.length > 0 && (
                                  <div className="mt-4">
                                    <h6 className="mb-3">Document Requests</h6>
                                    <Accordion activeKey={Array.from(openAccordions)} onSelect={(key) => {
                                      setOpenAccordions(prev => {
                                        const newSet = new Set(prev);
                                        if (newSet.has(key)) {
                                          newSet.delete(key);
                                        } else {
                                          newSet.add(key);
                                        }
                                        return newSet;
                                      });
                                    }}>
                                      {group.documentRequestAssignments.map((assignment, idx) => (
                                        <Accordion.Item key={`request-assign-${assignment.id}`} eventKey={`request-assign-${assignment.id}-${idx}`} className="border-0 custom-accordian-border mb-2">
                                          <div className="d-flex flex-row align-items-center justify-content-between py-3">
                                            <div className="d-flex flex-row align-items-center justify-content-between w-100 gap-2">
                                              <div className="d-flex flex-md-row flex-column gap-md-4 gap-2 align-items-center">
                                                <div className="d-flex flex-row align-items-center gap-2 me-3 me-md-0">
                                                  <Badge bg="secondary" className="rounded-2 p-2">
                                                    <i className="bi bi-file-earmark-plus-fill fs-4"></i>
                                                  </Badge>
                                                  <div>
                                                    <div className="fw-semibold">
                                                      {assignment.customName || assignment.documentRequest?.title || 'Document Request'}
                                                    </div>
                                                    {assignment.documentRequest?.description && (
                                                      <small className="text-muted">
                                                        {assignment.documentRequest.description.length > 77 ? (
                                                          assignment.documentRequest.description.slice(0, 77) + "..."
                                                        ) : (
                                                          assignment.documentRequest.description
                                                        )}
                                                      </small>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                              <div className="d-flex flex-sm-row flex-column align-items-sm-center align-items-start justify-content-end gap-2 me-3">
                                                <Badge bg={getAssignmentStatus(assignment) === 'completed' ? 'success' : 'secondary'}>
                                                  {getAssignmentStatus(assignment)}
                                                </Badge>
                                                {(() => {
                                                  const hasUploadedFiles = (uploadedFiles[assignment.id] || []).length > 0;
                                                  const hasSubmittedFiles = (getSubmittedFilesForAssignment[assignment.id] || []).length > 0;
                                                  return hasSubmittedFiles ? (
                                                    <Badge bg="success" size="sm">
                                                      <i className="bi bi-check-circle-fill me-1"></i>
                                                      Submitted
                                                    </Badge>
                                                  ) : hasUploadedFiles ? (
                                                    <Badge bg="warning" size="sm">
                                                      <i className="bi bi-clock me-1"></i>
                                                      Files Ready
                                                    </Badge>
                                                  ) : (
                                                    <Button size="sm" variant="outline-primary" onClick={() => openUploadModal(assignment)}>
                                                      <i className="bi bi-cloud-upload"></i>
                                                      <span className="d-none d-sm-inline ms-2">Upload</span>
                                                    </Button>
                                                  );
                                                })()}
                                              </div>
                                            </div>
                                            <Accordion.Button as="div" className="border-0 bg-transparent p-0" style={{ width: 'auto' }}>
                                            </Accordion.Button>
                                          </div>
                                          <Accordion.Body>
                                            <div className="d-flex justify-content-between align-items-center">
                                              <small className="text-muted">
                                                Due: {assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 'No due date'}
                                              </small>
                                            </div>

                                            {(() => {
                                              const assignmentFiles = uploadedFiles[assignment.id] || [];
                                              const submittedAssignmentFiles = getSubmittedFilesForAssignment[assignment.id] || [];
                                              const hasUploadedFiles = assignmentFiles.length > 0;
                                              const hasSubmittedFiles = submittedAssignmentFiles.length > 0;

                                              return (hasUploadedFiles || hasSubmittedFiles) ? (
                                                <div className="mt-3">
                                                  {hasUploadedFiles && (
                                                    <>
                                                      <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <h6 className="mb-0 fs-6">Uploaded Files</h6>
                                                        <Button
                                                          size="sm"
                                                          variant="success"
                                                          onClick={() => submitUploadedFiles(assignment.id)}
                                                          disabled={uploading}
                                                        >
                                                          {uploading ? 'Submitting...' : 'Submit'}
                                                        </Button>
                                                      </div>
                                                      <div className="list-group">
                                                        {assignmentFiles.map((fileData) => (
                                                          <div key={fileData.id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                                                            <div className="d-flex align-items-center gap-2">
                                                              <i className={`${getFileIcon(fileData.file.name)} fs-6`}></i>
                                                              <span className="small">{fileData.file.name}</span>
                                                              <small className="text-muted">({(fileData.file.size / 1024).toFixed(1)} KB)</small>
                                                            </div>
                                                            <Button
                                                              size="sm"
                                                              variant="outline-danger"
                                                              onClick={() => removeUploadedFile(assignment.id, fileData.id)}
                                                            >
                                                              <i className="bi bi-trash"></i>
                                                            </Button>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    </>
                                                  )}

                                                  {hasSubmittedFiles && (
                                                    <>
                                                      <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <h6 className="mb-0 fs-6">Submitted Files</h6>
                                                      </div>
                                                      <div className="list-group">
                                                        {submittedAssignmentFiles.map((fileData) => (
                                                          <div key={fileData.id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                                                            <div className="d-flex align-items-center gap-2">
                                                              <i className={`${getFileIcon(fileData.file.name)} fs-6 text-muted`}></i>
                                                              <span className="small text-muted">{fileData.file.name}</span>
                                                              <small className="text-muted">({(fileData.file.size / 1024).toFixed(1)} KB)</small>
                                                            </div>
                                                            <Badge bg={fileData.status === 'approved' ? 'success' : fileData.status === 'rejected' ? 'danger' : 'info'} className="ms-1">
                                                              {fileData.status}
                                                            </Badge>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    </>
                                                  )}
                                                </div>
                                              ) : null;
                                            })()}
                                          </Accordion.Body>
                                        </Accordion.Item>
                                      ))}
                                    </Accordion>
                                  </div>
                                )}
                              </>
                            )}
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    })}
                  </Accordion>
                  </>
                  )}
                  <div className="px-3 pt-3 pb-1 border-top">
                    <h5 className="h6 text-uppercase text-muted mb-0">Assigned Forms</h5>
                    <small className="text-muted">Open and complete your required forms.</small>
                  </div>
                  {standaloneFormAssignments.length > 0 ? (
                    <Accordion defaultActiveKey={['assigned-forms']} alwaysOpen className="mb-3">
                      <Accordion.Item eventKey="assigned-forms" className="border-0 rounded-0">
                        <Accordion.Header>
                          <span className="fw-semibold">Forms</span>
                        </Accordion.Header>
                        <Accordion.Body className="d-flex flex-column gap-2">
                          {standaloneFormAssignments.map((assignment) => {
                            const latestFormSubmission = getLatestMyFormSubmissionForAssignment(assignment.id);
                            const isReadOnlyForm = ['submitted', 'approved'].includes(String(latestFormSubmission?.status || '').toLowerCase());
                            return (
                              <div key={`form-assign-${assignment.id}`} className="border rounded-3 p-3 bg-light-subtle d-flex justify-content-between align-items-start gap-2">
                                <div>
                                  <div className="fw-semibold">{assignment.customName || assignment.form?.title || 'Form'}</div>
                                  <div className="text-muted small">{assignment.form?.description || 'Complete this required form.'}</div>
                                  <small className="text-muted d-block mt-1">
                                    Due: {assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 'No due date'}
                                  </small>
                                  {latestFormSubmission?.status === 'rejected' && latestFormSubmission?.reviewNotes ? (
                                    <Alert variant="warning" className="py-2 mt-2 mb-0">
                                      <strong>Rejection reason:</strong> {latestFormSubmission.reviewNotes}
                                    </Alert>
                                  ) : null}
                                </div>
                                <Button
                                  size="sm"
                                  variant="primary"
                                  onClick={() => {
                                    setActiveFormAssignment(assignment);
                                    setShowCompleteFormModal(true);
                                  }}
                                >
                                  {isReadOnlyForm ? 'View Form' : 'Open Form'}
                                </Button>
                              </div>
                            );
                          })}
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  ) : (
                    <div className="px-3 pb-3 text-muted small">No assigned forms.</div>
                  )}
                  </>
                )}

                {/* Footer Messages */}
                {hasSubmittedDocuments() && !canAccessApp() && (
                  <div className="mt-4 px-3 py-4 border-top bg-light" style={{ borderRadius: '0 0 1rem 1rem' }}>
                    <div className="text-center">
                      <i className="bi bi-clock-history text-primary me-2"></i>
                      <span className="text-muted">
                        Your documents have been submitted and sent to your program for review. 
                        Once approved, you will be able to access the rest of the application.
                      </span>
                    </div>
                  </div>
                )}

                {/* Continue to App Button */}
                {canAccessApp() && (
                  <div className="text-center mt-4 p-4 border-top d-flex justify-content-between align-items-center gap-2 bg-light">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-check-circle-fill text-success"></i>
                      All document requirements approved by your program! You can now access the application.
                    </div>
                    <Button 
                      variant="success" 
                      onClick={handleContinueToApp}
                    >
                      Continue to Application
                      <i className="bi bi-arrow-right ms-2"></i>
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <OnboardingUploadModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        onUpload={handleUploadMultiple}
        uploading={uploading}
        isDocumentUpload={false}
        isAssignmentUpload={true}
        categoryOptions={[]}
      />
      <OnboardingFormModal
        show={showCompleteFormModal}
        onHide={() => {
          setShowCompleteFormModal(false);
          setActiveFormAssignment(null);
        }}
        form={activeFormAssignment?.form || null}
        assignment={activeFormAssignment}
        initialData={getLatestMyFormSubmissionForAssignment(activeFormAssignment?.id)?.formData || {}}
        readOnly={['submitted', 'approved'].includes(String(getLatestMyFormSubmissionForAssignment(activeFormAssignment?.id)?.status || '').toLowerCase())}
        reviewNotes={getLatestMyFormSubmissionForAssignment(activeFormAssignment?.id)?.status === 'rejected'
          ? (getLatestMyFormSubmissionForAssignment(activeFormAssignment?.id)?.reviewNotes || '')
          : ''}
        onSubmit={(formData) => handleSubmitFormAssignment(activeFormAssignment, formData)}
      />
    </>
  );
};

export default DocumentCompletionPage;
